/**
 * Duration calculation utilities
 */

/**
 * Calculate expected output duration after crossfades
 *
 * Formula: totalDuration = (clipDuration * repeatCount) - (crossfadeDuration * (repeatCount - 1))
 *
 * Each crossfade "eats" crossfadeDuration of time because the clips overlap during the transition.
 *
 * @param {number} clipDuration - Duration of the input clip in seconds
 * @param {number} repeatCount - Number of repetitions (2-10)
 * @param {number} crossfadeDuration - Duration of each crossfade in seconds
 * @returns {number} Expected output duration in seconds
 */
export function calculateOutputDuration(clipDuration, repeatCount, crossfadeDuration) {
  if (repeatCount <= 1) {
    return clipDuration;
  }

  // Total time without crossfades
  const totalWithoutCrossfade = clipDuration * repeatCount;

  // Time "lost" to crossfade overlaps
  const crossfadeOverlap = crossfadeDuration * (repeatCount - 1);

  return totalWithoutCrossfade - crossfadeOverlap;
}

/**
 * Format output duration estimate as a display string
 *
 * @param {number} clipDuration - Duration of the input clip in seconds
 * @param {number} repeatCount - Number of repetitions
 * @param {number} crossfadeDuration - Duration of each crossfade in seconds
 * @returns {string} Formatted string like "30s × 5 = 144s output"
 */
export function formatDurationEstimate(clipDuration, repeatCount, crossfadeDuration) {
  const outputDuration = calculateOutputDuration(clipDuration, repeatCount, crossfadeDuration);

  const clipStr = formatSeconds(clipDuration);
  const outputStr = formatSeconds(outputDuration);

  return `${clipStr} × ${repeatCount} = ${outputStr} output`;
}

/**
 * Format seconds as a human-readable string
 *
 * @param {number} seconds - Duration in seconds
 * @returns {string} Formatted string (e.g., "30s", "2:30", "1:05:30")
 */
export function formatSeconds(seconds) {
  if (seconds < 0) return '0s';

  const totalSeconds = Math.round(seconds);

  if (totalSeconds < 60) {
    return `${totalSeconds}s`;
  }

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }

  return `${minutes}:${String(secs).padStart(2, '0')}`;
}

/**
 * Format seconds with decimal places for precise display
 *
 * @param {number} seconds - Duration in seconds
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted string (e.g., "30.5s")
 */
export function formatSecondsDecimal(seconds, decimals = 1) {
  if (seconds < 0) return '0s';
  return `${seconds.toFixed(decimals)}s`;
}

/**
 * Parse a duration string to seconds
 * Supports formats: "30s", "30", "2:30", "1:05:30"
 *
 * @param {string} str - Duration string
 * @returns {number|null} Duration in seconds or null if invalid
 */
export function parseDurationString(str) {
  if (!str || typeof str !== 'string') return null;

  // Remove trailing 's' if present
  const cleaned = str.trim().toLowerCase().replace(/s$/, '');

  // Try simple number first
  if (/^\d+(\.\d+)?$/.test(cleaned)) {
    return parseFloat(cleaned);
  }

  // Try HH:MM:SS or MM:SS format
  const parts = cleaned.split(':');
  if (parts.length === 2) {
    const [min, sec] = parts.map(Number);
    if (!isNaN(min) && !isNaN(sec)) {
      return min * 60 + sec;
    }
  } else if (parts.length === 3) {
    const [hr, min, sec] = parts.map(Number);
    if (!isNaN(hr) && !isNaN(min) && !isNaN(sec)) {
      return hr * 3600 + min * 60 + sec;
    }
  }

  return null;
}
